/**
 * Dashboard.tsx
 *
 * File-level:
 * - Main Dashboard page that composes multiple smaller dashboard components.
 * - Uses a centralized auth store (src/stores/useAuthStore.ts) as the single source of truth
 *   for logged-in user detection and auth token.
 *
 * Notes:
 * - The component initializes the auth store from localStorage on mount if the store is empty.
 * - All user references now come from useAuthStore to avoid duplicated localStorage reads.
 **/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Eye, Heart, MessageCircle, Sparkles, Search, User, Crown, X } from 'lucide-react';
import useShortlistCount from '../hooks/useShortlistCount';
import QuickSearchPopup from '../components/dashboard/QuickSearchPopup';
import { apiBase, fetchJSON } from '@/utils/api';
import useAuthStore from '@/stores/useAuthStore';

// Modular dashboard components
import HeaderSection from '../components/dashboard/HeaderSection';
import StatsGrid from '../components/dashboard/StatsGrid';
import ProfilePhotosSection from '../components/dashboard/ProfilePhotosSection';
import QuickActionsSection from '../components/dashboard/QuickActionsSection';
import MessagesCard from '@/components/MessagesCard';
import ChatModal from '../components/ChatModal';
import RecentActivitySection from '../components/dashboard/RecentActivitySection';
import ProfileCompletionSection from '../components/dashboard/ProfileCompletionSection';
import DailyTipsSection from '../components/dashboard/DailyTipsSection';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ProfileViewsModal from '../components/dashboard/ProfileViewsModal.tsx';
import ShortlistModal from '../components/dashboard/ShortlistModal';
import ExpressedInterestModal from '../components/dashboard/ExpressedInterestModal';
import MessagesModal from '../components/MessagesModal';

import { registerMessagesModalOpener, unregisterMessagesModalOpener } from '@/lib/modalService';

/**
 * Inline toast shape used by this page.
 */
interface InlineToast {
  /** Unique id for toast */
  id: string;
  /** Message to display */
  message: string;
}

/**
 * formatNumber
 *
 * @param n number | null
 * @returns formatted string or '—'
 */
function formatNumber(n: number | null): string {
  if (n === null || typeof n === 'undefined') return '—';
  try {
    return n.toLocaleString();
  } catch {
    return String(n);
  }
}

/**
 * ExpressedInterestChangeListenerProps
 *
 * @description Props for the small listener component that wires up a global
 * expressed-interest change event and performs optimistic updates or refreshes.
 */
interface ExpressedInterestChangeListenerProps {
  userId?: string | null;
  fetchExpressedInterestCount: (userId: string) => Promise<void>;
}

/**
 * ExpressedInterestChangeListener
 *
 * @description Small internal component that listens for window custom events
 * 'expressed-interest-changed'.
 */
function ExpressedInterestChangeListener({ userId, fetchExpressedInterestCount }: ExpressedInterestChangeListenerProps) {
  useEffect(() => {
    function onExpressChanged(e: Event) {
      try {
        const ce = e as CustomEvent;
        const detail = ce.detail || {};
        const delta = typeof detail.delta !== 'undefined' ? Number(detail.delta) : NaN;
        const refresh = Boolean(detail.refresh);

        if (refresh && userId) {
          fetchExpressedInterestCount(userId).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed refresh after expressed-interest-changed event', err);
          });
          return;
        }

        if (!Number.isNaN(delta)) {
          const updateEvent = new CustomEvent('__expressed_local_delta__', { detail: { delta } });
          window.dispatchEvent(updateEvent);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('expressed-interest-changed handler error', err);
      }
    }

    window.addEventListener('expressed-interest-changed', onExpressChanged as EventListener);
    return () => {
      window.removeEventListener('expressed-interest-changed', onExpressChanged as EventListener);
    };
  }, [userId, fetchExpressedInterestCount]);

  return null;
}

/**
 * Dashboard
 *
 * Component responsibilities:
 * - Loads current user from centralized auth store (useAuthStore). If store is empty it will
 *   attempt to initialize from localStorage and set the store, otherwise redirect to /login.
 * - Fetches user photos.
 * - Fetches profile views count and polls periodically.
 */
export default function Dashboard(): JSX.Element {
  const navigate = useNavigate();

  // Use centralized auth store for user and auth token.
  const user = useAuthStore((s) => s.user);
  const userIdFromStore = useAuthStore((s) => s.userId);
  const setStoreUser = useAuthStore((s) => s.setUser);
  const setStoreAuthToken = useAuthStore((s) => s.setAuthToken);

  // Basic page state (non-auth)
  const [userPhotos, setUserPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [photosLoading, setPhotosLoading] = useState<boolean>(true);
  const [showQuickSearch, setShowQuickSearch] = useState<boolean>(false);

  // Profile Views state
  const [profileViewsCount, setProfileViewsCount] = useState<number | null>(null);
  const [profileViewsLoading, setProfileViewsLoading] = useState<boolean>(false);

  // Expressed Interest state (live count)
  const [expressedCount, setExpressedCount] = useState<number | null>(null);
  const [expressedLoading, setExpressedLoading] = useState<boolean>(false);

  // Messages (unique senders) state
  const [messagesCount, setMessagesCount] = useState<number | null>(null);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);

  const [messagesError, setMessagesError] = useState<string | null>(null);

  // Polling ref for messages count (so we can clear interval on unmount)
  const messagesPollingRef = useRef<number | null>(null);

  /**
   * fetchMessagesCount
   *
   * @description Fetch conversation summaries for the current user and compute the number
   * of unique other users who currently have unread messages (server authoritative).
   * This implementation is tolerant to many backend shapes:
   *  - { count }
   *  - { cnt }
   *  - { data: { count } }
   *  - { totalUnread } / { unreadCount }
   *  - { senders: [{ senderId, unreadCount }] }
   *  - array of rows with unreadCount / unread
   *
   * If the DB helper or endpoint is missing, it falls back to 0 (no 404).
   *
   * @param userId string - the logged-in user's id
   */
  async function fetchMessagesCount(userId: string) {
    if (!userId) return;
    setMessagesLoading(true);
    setMessagesError(null);

    // Quick lightweight count endpoints to try first (fast, minimal payload)
    const lightweight = [
      `/api/chat/count/${encodeURIComponent(userId)}`,
      `/api/chat/count?userId=${encodeURIComponent(userId)}`,
      `/api/chat/unread-senders/count/${encodeURIComponent(userId)}`,
      `/api/chat/unread-senders/${encodeURIComponent(userId)}/count`
    ];

    // Broader candidate endpoints (kept for compatibility)
    const candidates = [
      `/api/chat/unread-senders/${encodeURIComponent(userId)}`,
      `api/chat/unread-senders/${encodeURIComponent(userId)}`,
      `/chat/unread-senders/${encodeURIComponent(userId)}`,
      `chat/unread-senders/${encodeURIComponent(userId)}`,
      `/api/chat/unread-senders?userId=${encodeURIComponent(userId)}`,
      // additional common alternatives
      `/api/chat/conversations/${encodeURIComponent(userId)}`,
      `/api/chat/conversations`,
      `/api/messages/unread/${encodeURIComponent(userId)}`,
      `/messages/unread/${encodeURIComponent(userId)}`,
      `/api/messages?userId=${encodeURIComponent(userId)}`
    ];

    let json: any = null;
    let lastErr: any = null;

    /**
     * deriveCountFromJson
     *
     * @description Derive a numeric unread count from a variety of JSON shapes.
     * Supports direct numbers, nested data, arrays (senders or items with unreadCount), and objects with `senders`/`items` arrays.
     */
    function deriveCountFromJson(data: any): number | null {
      try {
        if (!data) return null;
        const direct = ['count', 'cnt', 'totalUnread', 'unreadCount', 'unread', 'total', 'totalCount'];
        for (const key of direct) {
          if (typeof data[key] === 'number') return Number(data[key]);
        }
        if (data.data && typeof data.data === 'object') {
          for (const key of direct) {
            if (typeof data.data[key] === 'number') return Number(data.data[key]);
          }
        }
        if (Array.isArray(data)) {
          const arr = data as any[];
          if (arr.length === 0) return 0;
          if (typeof arr[0] === 'object' && (typeof arr[0].unreadCount !== 'undefined' || typeof arr[0].unread !== 'undefined')) {
            return arr.reduce((sum, it) => sum + Number(it.unreadCount ?? it.unread ?? 0), 0);
          }
          return arr.length;
        }
        for (const arrKey of ['senders', 'list', 'conversations', 'items']) {
          if (Array.isArray(data[arrKey])) {
            const arr = data[arrKey] as any[];
            if (arr.length === 0) return 0;
            if (typeof arr[0] === 'object' && (typeof arr[0].unreadCount !== 'undefined' || typeof arr[0].unread !== 'undefined')) {
              return arr.reduce((s, it) => s + Number(it.unreadCount ?? it.unread ?? 0), 0);
            }
            return arr.length;
          }
        }
        for (const k of Object.keys(data)) {
          if (typeof data[k] === 'number') return Number(data[k]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[deriveCountFromJson] error', err);
      }
      return null;
    }

    // Helper that tries a list of endpoints and returns the first parseable JSON
    async function tryEndpoints(list: string[]) {
      for (const p of list) {
        try {
          const res = await fetchJSON(p);
          if (typeof res !== 'undefined' && res !== null) {
            // reset failure counter on success
            messageFetchFailuresRef.current = 0;
            return res;
          }
        } catch (e) {
          lastErr = e;
          messageFetchFailuresRef.current = (messageFetchFailuresRef.current ?? 0) + 1;
        }
      }
      return null;
    }

    // 1) Try lightweight count endpoints first (fast)
    try {
      json = await tryEndpoints(lightweight);
    } catch (e) {
      // handled below
      lastErr = e;
    }

    // 2) If lightweight attempts failed, try the broader candidate list
    if (!json) {
      try {
        json = await tryEndpoints(candidates);
      } catch (e) {
        lastErr = e;
      }
    }

    // 3) If still no JSON, use cached fallback or set error
    if (!json) {
      try {
        const cached = localStorage.getItem('unreadSendersCount');
        if (cached !== null) {
          const parsed = Number(cached);
          if (!Number.isNaN(parsed)) {
            setMessagesCount(Math.max(0, Math.floor(parsed)));
            setMessagesLoading(false);
            return;
          }
        }
        const errMsg = lastErr ? String(lastErr) : 'no endpoint responded';
        setMessagesError(`Failed to fetch unread senders: ${errMsg}`);
        setMessagesLoading(false);
        return;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[fetchMessagesCount] fallback handling failed', err);
        setMessagesLoading(false);
        setMessagesError('Fallback failure when reading cached message count');
        return;
      }
    }

    // 4) Parse the response
    try {
      const count = deriveCountFromJson(json);
      if (count === null || Number.isNaN(count)) {
        // eslint-disable-next-line no-console
        console.warn('[fetchMessagesCount] unable to derive count from response, response:', json);
        setMessagesError('Unable to parse unread senders count from server response');
        // Keep existing count rather than force 0
      } else {
        setMessagesCount(Math.max(0, Math.floor(Number(count))));
        try {
          localStorage.setItem('unreadSendersCount', String(Math.max(0, Math.floor(Number(count)))));
        } catch {
          // ignore storage errors
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[fetchMessagesCount] processing response failed', err);
      setMessagesError(String(err));
    } finally {
      setMessagesLoading(false);
    }
  }

  // Profile Views Modal state
  const [profileViewsModalOpen, setProfileViewsModalOpen] = useState<boolean>(false);
  // Shortlist Modal state
  const [shortlistModalOpen, setShortlistModalOpen] = useState<boolean>(false);
  // Expressed Interest Modal state
  const [expressedInterestModalOpen, setExpressedInterestModalOpen] = useState<boolean>(false);
  // Messages Modal state (opened via StatsGrid 'Messages' or other UI)
  const [messagesModalOpen, setMessagesModalOpen] = useState<boolean>(false);

  // Chat modal (opened from dashboard MessagesCard or profile)
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [chatConversationWith, setChatConversationWith] = useState<{ id: string; name: string } | null>(null);

  // Inline toasts
  const [toasts, setToasts] = useState<InlineToast[]>([]);

  // Polling refs to clear intervals
  const pollingRef = useRef<number | null>(null);
  const expressedPollingRef = useRef<number | null>(null);
  // Timestamp ref used to ignore the immediate click that opened the modal.
  const modalOpenTimestampRef = useRef<number | null>(null);

  // Keep previous count to detect increments for toasts
  const prevCountRef = useRef<number | null>(null);
  const messagesPollingCleanupRef = useRef<(() => void) | null>(null);
  const messageFetchFailuresRef = useRef<number>(0);
  
  // Listen for server PUSH of unread-senders (dispatched by useChat and also by server)
  useEffect(() => {
    function onUnreadSenders(e: Event) {
      try {
        const ce = e as CustomEvent;
        const detail = ce.detail || {};
        const cnt = Number(detail.count ?? 0);
        if (!Number.isNaN(cnt)) {
          setMessagesCount(cnt);
          setMessagesLoading(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('unread-senders-changed handler error', err);
      }
    }

    window.addEventListener('unread-senders-changed', onUnreadSenders as EventListener);
    return () => {
      window.removeEventListener('unread-senders-changed', onUnreadSenders as EventListener);
    };
  }, []);

  /**
   * showToast
   *
   * Adds a small inline toast that auto-dismisses after 3s.
   *
   * @param message string
   */
  function showToast(message: string) {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }

  /**
   * fetchProfileViewsCount
   *
   * Fetches profile view counts for given userId and updates state.
   *
   * @param userId string
   */
  async function fetchProfileViewsCount(userId: string) {
    if (!userId) return;
    setProfileViewsLoading(true);
    try {
      const json = await fetchJSON(`/dashboard/profile-views/count/${encodeURIComponent(userId)}`);
      let newCount: number | null = null;
      if (json && (json as any).data) {
        newCount = Number((json as any).data.totalViews ?? null);
      } else if (json && typeof (json as any).totalViews !== 'undefined') {
        newCount = Number((json as any).totalViews ?? null);
      }

      if (typeof newCount === 'number' && !Number.isNaN(newCount)) {
        const prev = prevCountRef.current;
        if (prev === null) {
          // initialize without toast
        } else if (newCount > prev) {
          const diff = newCount - prev;
          showToast(`+${diff} new view${diff > 1 ? 's' : ''}`);
        }
        prevCountRef.current = newCount;
        setProfileViewsCount(newCount);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch profile views count:', err);
    } finally {
      setProfileViewsLoading(false);
    }
  }

  /**
   * fetchExpressedInterestCount
   *
   * @param toUserId string - the recipient (logged-in user) for whom we want the count
   */
  async function fetchExpressedInterestCount(toUserId: string) {
    if (!toUserId) return;
    setExpressedLoading(true);
    try {
      const { getExpressedInterestCount } = await import('../services/interestApi');
      const result = await getExpressedInterestCount(toUserId, 'Pending');
      const count = Number(result?.count ?? 0);
      setExpressedCount(isNaN(count) ? 0 : count);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch expressed interest count:', err);
    } finally {
      setExpressedLoading(false);
    }
  }

  /**
   * Optimistic update listener for profile-view-recorded events.
   */
  useEffect(() => {
    function onProfileViewRecorded(e: Event) {
      try {
        const ce = e as CustomEvent;
        const detail = ce.detail || {};
        const profileId = detail.profileId;
        const increment = Number(detail.increment ?? 1);
        if (!profileId || !user || !user.userId) return;
        if (String(profileId) === String(user.userId)) {
          setProfileViewsCount((prev) => {
            const next = (prev ?? 0) + increment;
            const prevVal = prev ?? 0;
            if (next > prevVal) {
              showToast(`+${increment} new view`);
              prevCountRef.current = next;
            }
            return next;
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('profile-view-recorded handler error', err);
      }
    }

    window.addEventListener('profile-view-recorded', onProfileViewRecorded as EventListener);
    return () => {
      window.removeEventListener('profile-view-recorded', onProfileViewRecorded as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * Listen for local optimistic deltas caused by an expressed-interest change event.
   */
  useEffect(() => {
    function onLocalDelta(e: Event) {
      try {
        const ce = e as CustomEvent;
        const detail = ce.detail || {};
        const delta = Number(detail.delta ?? 0);
        if (Number.isNaN(delta)) return;
        setExpressedCount((prev) => {
          const base = prev ?? 0;
          const next = base + delta;
          return next < 0 ? 0 : next;
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('expressed local delta handler error', err);
      }
    }

    window.addEventListener('__expressed_local_delta__', onLocalDelta as EventListener);
    return () => {
      window.removeEventListener('__expressed_local_delta__', onLocalDelta as EventListener);
    };
  }, []);

  /**
   * Initialize auth store from localStorage if store is empty and redirect if not authenticated.
   */
  useEffect(() => {
    // If store already has user, respect it.
    const existingUser = useAuthStore.getState().user;
    const existingToken = useAuthStore.getState().authToken;
    const authToken = existingToken ?? localStorage.getItem('authToken');
    const userDataRaw = existingUser ? JSON.stringify(existingUser) : localStorage.getItem('userData');

    if (!authToken || !userDataRaw) {
      navigate('/login');
      return;
    }

    let parsedUser: any | null = null;
    try {
      parsedUser = existingUser ?? JSON.parse(userDataRaw as string);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse userData from localStorage', e);
      navigate('/login');
      return;
    }

    // Ensure the central store is populated
    if (!existingUser) {
      setStoreUser(parsedUser);
    }
    if (!existingToken) {
      setStoreAuthToken(authToken);
    }

    // Fetch photos and finish loading states
    fetchUserPhotos(parsedUser.userId)
      .catch(() => {
        /* swallow - fallback handled inside */
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  /**
   * Start polling for profile views and expressed-interest when user is available.
   */
  useEffect(() => {
    if (!user || !user.userId) return;

    // Profile views polling
    fetchProfileViewsCount(user.userId);
    if (pollingRef.current === null) {
      const id = window.setInterval(() => {
        fetchProfileViewsCount(user.userId);
      }, 15000);
      pollingRef.current = id;
    }

    // Expressed interest polling
    fetchExpressedInterestCount(user.userId);
    if (expressedPollingRef.current === null) {
      const id2 = window.setInterval(() => {
        fetchExpressedInterestCount(user.userId);
      }, 30000);
      expressedPollingRef.current = id2;
    }

      // Messages unique-senders polling (server authoritative) - resilient runner with backoff
    (function startMessagesPolling() {
      let cancelled = false;

      // Runner uses setTimeout chain so we can vary delay on failures (exponential backoff up to 2 minutes)
      async function tick() {
        if (cancelled) return;
        try {
          await fetchMessagesCount(user.userId);
        } catch (e) {
          // fetchMessagesCount handles errors internally; this is defensive
          // eslint-disable-next-line no-console
          console.warn('[messagesPolling] tick error', e);
        }
        if (cancelled) return;

        // compute delay based on consecutive failures
        const failures = messageFetchFailuresRef.current ?? 0;
        const base = 30000; // 30s base
        const delay = failures <= 0 ? base : Math.min(120000, base * Math.pow(2, Math.min(failures - 1, 4)));
        messagesPollingRef.current = window.setTimeout(tick, delay) as unknown as number;
      }

      // start immediate tick
      tick();

      // cleanup function registered via closure
      const cleanup = () => {
        cancelled = true;
        if (messagesPollingRef.current !== null) {
          clearTimeout(messagesPollingRef.current);
          messagesPollingRef.current = null;
        }
      };

      // attach cleanup to unmount via effect cleanup (we simulate return behavior by pushing to a list)
      // Since we're inside a useEffect, we can't return here — the outer effect will handle cleanup below.
      // instead we store the cleanup function so outer effect can call it.
      messagesPollingCleanupRef.current = cleanup;
    })();

    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (expressedPollingRef.current !== null) {
        window.clearInterval(expressedPollingRef.current);
        expressedPollingRef.current = null;
      }
      if (messagesPollingRef.current !== null) {
        window.clearInterval(messagesPollingRef.current);
        messagesPollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * fetchUserPhotos
   *
   * Attempts to load photos via dedicated dashboard API; falls back to folder listing.
   *
   * @param userId string
   */
  async function fetchUserPhotos(userId: string) {
    setPhotosLoading(true);

    const resolvedBase = String(apiBase() ?? '').replace(/\/$/, '');
    const mediaHost = resolvedBase.replace(/\/api$/, '');

    try {
      const result: any = await fetchJSON(`/dashboard/user-photos/${encodeURIComponent(userId)}?_=${Date.now()}`);
      if (result && result.success && Array.isArray(result.photos) && result.photos.length > 0) {
        setUserPhotos(result.photos);
        setPhotosLoading(false);
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('dashboard photos API failed, falling back to folder', err);
    }

    try {
      const res2json: any = await fetchJSON(`/user-files/${encodeURIComponent(userId)}/Photos`);
      if (res2json && res2json.success && Array.isArray(res2json.files) && res2json.files.length > 0) {
        const photosFromFolder = res2json.files.map((file: any, idx: number) => ({
          id: `folder_${file.fileName}_${idx}`,
          src: `${mediaHost}/Media/${userId}/Photos/${file.fileName}`,
          fileName: file.fileName,
          fileSize: file.fileSize,
          uploadDate: file.uploadDate,
          isProfileDP: idx === 0
        }));
        setUserPhotos(photosFromFolder);
        setPhotosLoading(false);
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('photos folder fallback failed', err);
    }

    setUserPhotos([]);
    setPhotosLoading(false);
  }

  /**
   * handleStatClick
   *
   * Called by StatsGrid when a stat card is clicked or activated by keyboard.
   * Opens the appropriate modal based on the label or index.
   */
  function handleStatClick(label: string, index: number) {
    const normalized = (label || '').trim().toLowerCase();

    if (label === 'Profile Views' || index === 0) {
      setProfileViewsModalOpen(true);
    } else if (label === 'Shortlisted') {
      setShortlistModalOpen(true);
    } else if (normalized === 'messages' || index === 2) {
      // Open the consolidated MessagesModal directly
      setMessagesModalOpen(true);
    } else if (normalized.includes('express')) {
      setExpressedInterestModalOpen(true);
    }
  }

  // Shortlist count (unique profileIds) hook - uses logged-in user id from store
  const { count: shortlistCount, loading: shortlistLoading } = useShortlistCount(user?.userId ?? null);

  // Stats array uses store data
  const stats = [
    {
      label: 'Profile Views',
      value: profileViewsLoading ? '—' : (profileViewsCount !== null ? formatNumber(profileViewsCount) : '—'),
      icon: Eye,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
      trend: '+12%'
    },
    {
      label: 'Shortlisted',
      value: shortlistLoading ? '—' : (shortlistCount !== null ? formatNumber(shortlistCount) : '—'),
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50',
      trend: '+8%'
    },
    {
      label: 'Messages',
      value: messagesLoading ? '—' : (messagesCount !== null ? formatNumber(messagesCount) : '—'),
      icon: MessageCircle,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
      trend: '+15%'
    },
    {
      label: 'Expressed Interest',
      value: expressedLoading ? '—' : (expressedCount !== null ? formatNumber(expressedCount) : '—'),
      icon: Sparkles,
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-gradient-to-br from-purple-50 to-violet-50',
      trend: '+20%'
    }
  ];

  // Quick actions
  const quickActions = [
    {
      title: 'Find Matches',
      description: 'Discover compatible profiles',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
      action: () => navigate('/search')
    },
    {
      title: 'My Profile',
      description: 'View and edit your profile',
      icon: User,
      color: 'from-green-500 to-emerald-500',
      action: () => navigate('/profile')
    },
    {
      title: 'Premium',
      description: 'Unlock exclusive features',
      icon: Crown,
      color: 'from-amber-500 to-orange-500',
      action: () => alert('Premium features coming soon!')
    }
  ];

  // Recent activity (static fallback)
  const recentActivity = [
    {
      id: '1',
      type: 'profile_view',
      message: 'Priya Sharma viewed your profile',
      time: '2 hours ago',
      user: 'RJBD240115P1S1'
    },
    {
      id: '2',
      type: 'interest_received',
      message: 'Anjali Patel sent you interest',
      time: '1 day ago',
      user: 'RJBD240118A1P1'
    },
    {
      id: '3',
      type: 'match_suggested',
      message: 'New match suggested based on your preferences',
      time: '3 days ago',
      user: 'RJBD240116N1G1'
    }
  ];

  // Current display picture (first photo flagged as DP)
  const currentDP = userPhotos.find((p) => p.isProfileDP) || userPhotos[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-blue-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <HeaderSection user={user} currentDP={currentDP} photosLoading={photosLoading} onFindMatchesClick={() => setShowQuickSearch(true)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <StatsGrid stats={stats} onStatClick={handleStatClick} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <ProfilePhotosSection userPhotos={userPhotos} photosLoading={photosLoading} navigate={navigate} />

            <QuickActionsSection quickActions={quickActions} />

            <Card>
              <CardContent>
                <div className="text-center text-sm text-gray-500">Explore your dashboard features and controls here.</div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <RecentActivitySection recentActivity={undefined} />

            <Card className="border-2 border-gray-200 bg-white shadow-sm">
              <CardContent className="p-4">
                <div className="text-left">
                  <h4 className="font-semibold text-gray-900">Improve your matches</h4>
                  <p className="text-sm text-gray-600">Use curated tips and profile improvements to increase visibility.</p>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ProfileCompletionSection completionPercentage={85} />
              <DailyTipsSection navigate={navigate} />
            </div>

            <Card className="border-2 border-gray-200 bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="text-center">
                  <h3 className="text-lg font-bold text-gray-900">Stay on top of your profile activity</h3>
                  <p className="text-sm text-gray-600 mt-2">Check recent interactions and update your preferences for better matches.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Quick Search Popup */}
      <QuickSearchPopup isOpen={showQuickSearch} onClose={() => setShowQuickSearch(false)} />

      {/* Profile Views Modal */}
      <ProfileViewsModal
        isOpen={profileViewsModalOpen}
        onClose={() => setProfileViewsModalOpen(false)}
        profileId={user?.userId ?? null}
        days={14}
        pollInterval={20000}
      />

      {/* Shortlist Modal */}
      <ShortlistModal
        isOpen={shortlistModalOpen}
        onClose={() => setShortlistModalOpen(false)}
        profileId={user?.userId ?? null}
      />

      {/* Expressed Interest Modal */}
      {expressedInterestModalOpen && (
        <ExpressedInterestModal
          toUserId={user?.userId ?? null}
          onClose={() => {
            setExpressedInterestModalOpen(false);
            if (user?.userId) {
              fetchExpressedInterestCount(user.userId);
            }
          }}
        />
      )}

      {/* MessagesModal - consolidated message list */}
      <MessagesModal
        open={messagesModalOpen}
        onClose={() => setMessagesModalOpen(false)}
        onViewProfile={(partnerId: string) => {
          // Prefer in-app profile view. If your app expects /profile/:id route, use navigate.
          setMessagesModalOpen(false);
          navigate(`/profile/${partnerId}`);
        }}
        onChat={(partnerId: string) => {
          // Open chat modal for selected partner and close messages modal.
          setMessagesModalOpen(false);
          setChatConversationWith({ id: partnerId, name: '' });
          setChatOpen(true);
        }}
        onRefresh={() => {
          if (user?.userId) {
            fetchMessagesCount(user.userId).catch(() => {});
          }
        }}
        navigateToSearch={() => {
          setMessagesModalOpen(false);
          navigate('/search');
        }}
      />

      {/* Chat Modal (dashboard) */}
      {chatOpen && chatConversationWith && (
        <ChatModal
          otherUserId={chatConversationWith.id}
          otherDisplayName={chatConversationWith.name}
          visible={true}
          onClose={() => {
            setChatOpen(false);
            setChatConversationWith(null);
            // Refresh messages count after closing chat (best-effort)
            if (user?.userId) {
              fetchMessagesCount(user.userId).catch(() => {});
            }
          }}
        />
      )}

      {/* Expressed interest listener */}
      <ExpressedInterestChangeListener userId={user?.userId ?? null} fetchExpressedInterestCount={fetchExpressedInterestCount} />

      {/* Inline Toasts */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="bg-slate-800 text-white px-4 py-2 rounded shadow-lg text-sm">
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}